import { notify } from '@/helpers';
import routeNames from '@/routes/routeNames';
import { AuthStore } from '@/state/AuthenticationStore';
import { emailMarketingStore } from '@/state/EmailMarketingStore';
import { replaceRouteParam } from '@/utils/routes';
import React, { useEffect, useRef, useState } from 'react';
import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { OutletContextType } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { useEmailMarketing } from '@/hooks/useEmailMarketing';
import { fetchRequestWithToken } from '@/config/axios';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ChevronDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const InFlowEmailBuilder = () => {
  const emailEditorRef = useRef<EditorRef>(null);
  const unlayer = emailEditorRef.current?.editor;
  const { channel } = useParams();
  const { isNextClicked, setIsNextClicked } =
    useOutletContext<OutletContextType>();
  const navigate = useNavigate();
  const { saveTemplate } = useEmailMarketing();
  const [type, setType] = useState('');
  const [name, setName] = useState('');
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const options = [
    { value: 'autumn', label: 'Autumn' },
    { value: 'spring', label: 'Spring' },
    { value: 'summer', label: 'Summer' },
    { value: 'winter', label: 'Winter' },
    { value: 'anniversary', label: 'Anniversary' },
    { value: 'backtoschool', label: 'Back to School' },
    { value: 'birthday', label: 'Birthday' },
    { value: 'blackfriday', label: 'Black Friday' },
    { value: 'christmas', label: 'Christmas' },
    { value: 'cybermonday', label: 'Cyber Monday' },
    { value: 'easter', label: 'Easter' },
  ];

  const toggleOption = (value: string) => {
    setSelectedValues(prev =>
      prev.includes(value)
        ? prev.filter(item => item !== value)
        : [...prev, value],
    );
  };

  const goNext = () => {
    navigate(
      replaceRouteParam(
        `${routeNames.dashboard.emailMarketing}/campaign-flow/${channel}/campaign-message`,
        'workspaceId',
        AuthStore.user_workspace_info?.active_workspace?.workspace?._id,
      ),
    );
  };

  useEffect(() => {
    if (isNextClicked) {
      goNext();
      saveDesign();
      setIsNextClicked(false);
    }
  }, [isNextClicked]);

  const saveDesign = () => {
    const randomUuid = uuidv4();
    const randomNumbers = randomUuid?.replace(/\D/g, '')?.slice(0, 6);
    unlayer?.exportHtml(async data => {
      const { design, html } = data;
      const { rows } = design.body;
      let isEmpty = true;
      label: for (let i = 0; i < rows?.length; i++) {
        const { columns } = rows[i];
        for (let j = 0; j < columns?.length; j++) {
          const { contents } = columns[j];
          if (contents?.length) {
            isEmpty = false;
            break label;
          }
        }
      }
      if (isEmpty) {
        notify(
          'error',
          'Please add content to the email template builder before saving',
        );
        return;
      }
      try {
        emailMarketingStore.setLoading(true);
        if (!design || !html) {
          notify('error', 'Invalid design data');
          return;
        }
        const template = {
          name: name ? name : `Template #${randomNumbers}`,
          type: type || 'basic layout',
          html: html,
          json: design,
          status: 'Draft',
        };
        emailMarketingStore.setTemplateOnDemand(template);
        saveTemplate(template);
      } catch (error) {
        notify('error', 'Error: Failed to save template');
      }
    });
  };

  const onReady: EmailEditorProps['onReady'] = unlayer => {
    unlayer.registerCallback('image', async function (file: any, done: any) {
      try {
        done({ progress: 50, url: '' });
        const workspaceId =
          AuthStore.user_workspace_info?.active_workspace?.workspace?._id;
        const response = await fetchRequestWithToken({
          url: `/upload/${workspaceId}`,
          method: 'POST',
          headers: { 'Content-Type': 'multipart/form-data' },
          data: {
            file: file?.attachments[0],
          },
        });
        done({ progress: 100, url: response?.data?.url });
      } catch (error) {
        notify('error', error as string);
        done({ progress: 0, url: '' });
      }
    });
  };
  return (
    <div className='flex w-full pb-14 h-full'>
      <div>
        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant={'outline'}
              className='w-full md:w-auto hover:bg-gray-250 rounded-none  bg-white text-black'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={1.5}
                stroke='currentColor'
                className='size-10'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5'
                />
              </svg>
            </Button>
          </SheetTrigger>
          <SheetContent
            side={'left'}
            className='w-[300px] sm:w-[400px] max-h-screen flex flex-col'
          >
            <SheetHeader>
              <SheetTitle>Template</SheetTitle>
            </SheetHeader>
            <ScrollArea className='flex-grow py-4'>
              <Input
                value={name}
                onChange={e => {
                  setName(e.target.value);
                }}
                placeholder='Template Name'
                type='text'
                className=' py-2 w-full focus-visible:outline-none focus-visible:ring-0
                                focus-visible:ring-ring mb-5 focus-visible:ring-offset-0 focus-within:border focus-within:border-secondary'
              />
              <Select value={type} onValueChange={value => setType(value)}>
                <SelectTrigger
                  className='focus:ring-0 focus:ring-offset-0
                               focus-within:border mb-5 focus-within:border-secondary'
                >
                  <SelectValue placeholder='Template type' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='basic layout'>Basic layout</SelectItem>
                  <SelectItem value='deals'>Deals</SelectItem>
                  <SelectItem value='confirmation'>Confirmation</SelectItem>
                  <SelectItem value='outreach'>Outreach</SelectItem>
                  <SelectItem value='reminders'>Reminders</SelectItem>
                  <SelectItem value='sales'>Sales</SelectItem>
                </SelectContent>
              </Select>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    className='w-full flex items-center justify-between  capitalize'
                  >
                    <span className='truncate overflow-hidden whitespace-nowrap'>
                      {selectedValues?.length > 0
                        ? selectedValues?.join(', ')
                        : 'Select Seasons & Holidays'}
                    </span>
                    <ChevronDown className='h-4 w-4 text-black/50 flex-shrink-0 ml-2' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-[var(--radix-popover-trigger-width)]'>
                  <div className='flex flex-col gap-2'>
                    {options?.map(option => (
                      <label
                        key={option?.value}
                        className='flex items-center space-x-2 cursor-pointer'
                      >
                        <Checkbox
                          checked={selectedValues?.includes(option?.value)}
                          onCheckedChange={() => toggleOption(option?.value)}
                        />
                        <span>{option?.label}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </ScrollArea>

            <SheetFooter className='flex !justify-start items-center'>
              <SheetClose>
                <Button type='button' variant='outline'>
                  Cancel
                </Button>
              </SheetClose>
              <Button
                onClick={() => setIsNextClicked(true)}
                type='button'
                className='text-white bg-secondary/90 hover:bg-secondary/80'
                disabled={!name}
              >
                Save Template
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>
      <EmailEditor ref={emailEditorRef} onReady={onReady} />
    </div>
  );
};

export default InFlowEmailBuilder;
